import axios from "axios";
import type { LoginRequest, LoginResponse, User } from "@/types";

// Use the main API URL for auth (backend now handles auth)
const AUTH_API_URL =
  import.meta.env.VITE_AUTH_API_URL ||
  import.meta.env.VITE_API_BASE_URL ||
  "http://localhost:5000";

export const authService = {
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    clearSession();
    const response = await axios.post<LoginResponse>(
      `${AUTH_API_URL}/api/auth/login`,
      credentials
    );

    if (response.data.success && response.data.requiresPasswordChange && response.data.passwordChangeToken)
      return response.data;
    return storeSession(response.data);
  },

  async changeInitialPassword(passwordChangeToken: string, newPassword: string): Promise<LoginResponse> {
    const response = await axios.post<LoginResponse>(`${AUTH_API_URL}/api/auth/initial-password`, {
      passwordChangeToken, newPassword,
    });
    return storeSession(response.data);
  },

  async restoreSession(): Promise<User | null> {
    const user = this.getCurrentUser();
    const token = this.getToken();
    if (!user || !token) { clearSession(); return null; }
    try {
      await axios.get(`${AUTH_API_URL}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
      return user;
    } catch {
      if (this.getToken() === token) clearSession();
      return null;
    }
  },

  logout() {
    clearSession();
    window.location.href = "/login";
  },

  getCurrentUser(): User | null {
    const userJson = localStorage.getItem("user");
    if (!userJson) return null;
    try {
      const parsed = JSON.parse(userJson) as User | (User & { role?: string });
      return normalizeUser(parsed);
    } catch {
      return null;
    }
  },

  getToken(): string | null {
    return localStorage.getItem("token");
  },

  isAuthenticated(): boolean {
    return !!this.getToken();
  },

  isManager(): boolean {
    const user = this.getCurrentUser();
    return user?.roles?.includes("manager") ?? false;
  },
};

function clearSession() {
  localStorage.removeItem("token");
  localStorage.removeItem("user");
}

function storeSession(response: LoginResponse): LoginResponse {
  const user = normalizeUser(response.user);
  if (!response.success || !response.token || !user || response.requiresPasswordChange)
    throw new Error("Inloggen mislukt. Controleer je gegevens of neem contact op met de beheerder.");
  localStorage.setItem("token", response.token);
  localStorage.setItem("user", JSON.stringify(user));
  return { ...response, user };
}

function normalizeUser(
  user: User | (User & { role?: string }) | undefined | null
): User | null {
  if (!user) return null;
  const roles = Array.isArray((user as any)?.roles)
    ? (user as any).roles
    : (user as any)?.role
    ? [(user as any).role]
    : [];

  return {
    ...user,
    roles,
  };
}
